import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { ArrowDropDownIcon, Text } from 'toonswap-uikit'

const DropDownHeader = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 16px;
  background: #2c3537;
  border-radius: 12px;
  border: 2px solid #252c2d;
  transition: border-radius 0.15s;
`

const HeaderLabelWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const DropDownListContainer = styled.div`
  width: 100%;
  min-width: 210px;
  height: 0;
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;
  z-index: ${({ theme }) => theme.zIndices.dropdown};
  transition:
    transform 0.15s,
    opacity 0.15s;
  transform: scaleY(0);
  transform-origin: top;
  opacity: 0;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 210px;
  }
`

const DropDownContainer = styled.div<{
  isOpen: boolean
  width: number
  height: number
  $fullWidthOnMobile?: boolean
}>`
  cursor: pointer;
  width: ${({ width }) => width}px;
  position: relative;
  height: 40px;
  min-width: 210px;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 210px;
  }

  ${({ $fullWidthOnMobile }) =>
    $fullWidthOnMobile &&
    css`
      @media (max-width: 768px) {
        width: 100% !important;
        min-width: 0;
      }
    `}

  ${(props) =>
    props.isOpen &&
    css`
      ${DropDownHeader} {
        box-shadow:
          inset 1px 1px 0 rgba(255, 255, 255, 0.08),
          inset -1px 0 0 rgba(0, 0, 0, 0.25);
        border-radius: 12px 12px 0 0;
      }

      ${DropDownListContainer} {
        height: auto;
        transform: scaleY(1);
        opacity: 1;
        background: #2b3236;
        border: 2px solid #252c2d;
        border-top-width: 0;
        border-radius: 0 0 12px 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      }
    `}

  svg {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #ffffff;
  }
`

const DropDownList = styled.ul`
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  z-index: ${({ theme }) => theme.zIndices.dropdown};
`

const ListItem = styled.li`
  list-style: none;
  padding: 8px 16px;
  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`

export interface SelectProps {
  options: OptionProps[]
  onChange?: (option: OptionProps) => void
  prefixLabel?: string
  fullWidthOnMobile?: boolean
}

export interface OptionProps {
  label: string
  value: any
}

const Select: React.FunctionComponent<SelectProps> = ({
  options,
  onChange,
  prefixLabel,
  fullWidthOnMobile = false,
}) => {
  const containerRef = useRef(null)
  const dropdownRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState(options[0])
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  const toggling = () => setIsOpen(!isOpen)

  const onOptionClicked = (option: OptionProps) => () => {
    setSelectedOption(option)
    setIsOpen(false)

    if (onChange) {
      onChange(option)
    }
  }

  useEffect(() => {
    setContainerSize({
      width: dropdownRef.current?.offsetWidth ?? 0,
      height: dropdownRef.current?.offsetHeight ?? 0,
    })
  }, [])

  return (
    <DropDownContainer isOpen={isOpen} ref={containerRef} {...containerSize} $fullWidthOnMobile={fullWidthOnMobile}>
      {containerSize.width !== 0 && (
        <DropDownHeader onClick={toggling}>
          <HeaderLabelWrap>
            {prefixLabel ? <Text color="#d0d6d7">{prefixLabel}</Text> : null}
            <Text color="#b0b0b0">{selectedOption.label}</Text>
          </HeaderLabelWrap>
        </DropDownHeader>
      )}
      <ArrowDropDownIcon color="#ffffff" onClick={toggling} />
      <DropDownListContainer>
        <DropDownList ref={dropdownRef}>
          {options.map((option) =>
            option.label !== selectedOption.label ? (
              <ListItem onClick={onOptionClicked(option)} key={option.label}>
                <Text color="#b0b0b0">{option.label}</Text>
              </ListItem>
            ) : null,
          )}
        </DropDownList>
      </DropDownListContainer>
    </DropDownContainer>
  )
}

export default Select
